import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin, Subscription } from 'rxjs';
import { AppConfirmService } from 'src/app/shared/services/app-confirm/app-confirm.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { ChunkService } from 'src/app/views/content/chunk/chunk.service';
import { ContentStateService } from 'src/app/views/content/content-state.service';

@Component({
  selector: 'app-bulk-action-bar',
  standalone: true,
  imports: [TranslateModule, MatButtonModule, MatIconModule, MatTooltipModule, MatCheckboxModule],
  template: `
    @if (contentState.isMultiSelectMode()) {
      <div class="bulk-action-bar" [@slideUp]>
        <div class="selected-count">
          <mat-checkbox
            [checked]="isAllSelected()"
            [indeterminate]="isIndeterminate()"
            (change)="toggleSelectAll($event.checked)"
            [matTooltip]="(isAllSelected() ? 'Global.DeselectAll' : 'Global.SelectAll') | translate"
            matTooltipPosition="above"
            color="primary"
          >
          </mat-checkbox>
          <span>{{ contentState.selectedChunks().length }} {{ 'Global.Selected' | translate }}</span>
        </div>

        <div class="divider"></div>

        <div class="actions">
          <button mat-button class="cancel-btn" (click)="contentState.exitMultiSelectMode()">
            {{ 'Global.Cancel' | translate }}
          </button>

          <button mat-icon-button btnType="action-icon" [matTooltip]="'Global.Translate' | translate" matTooltipPosition="above" [disabled]="contentState.selectedChunks().length === 0" (click)="translateSelected()">
            <mat-icon>translate</mat-icon>
          </button>

          <button mat-icon-button btnType="action-icon" [matTooltip]="'Global.GenerateAudio' | translate" matTooltipPosition="above" [disabled]="contentState.selectedChunks().length === 0" (click)="generateAudioForSelected()">
            <mat-icon>volume_up</mat-icon>
          </button>

          <button mat-flat-button btnType="danger" [disabled]="contentState.selectedChunks().length === 0" (click)="deleteSelected()">
            {{ 'Global.Delete' | translate }}
          </button>
        </div>
      </div>
    }
  `,
  styles: [
    `
      .bulk-action-bar {
        position: fixed;
        bottom: 24px;
        left: 50%;
        transform: translateX(-50%);
        background-color: var(--skz-background-color-surface);
        border: 1px solid var(--skz-border-color-main);
        border-radius: 14px;
        box-shadow:
          0 8px 24px rgba(0, 0, 0, 0.08),
          0 2px 8px rgba(0, 0, 0, 0.05);
        padding: 8px 16px;
        display: flex;
        align-items: center;
        gap: 12px;
        z-index: 1000;

        .selected-count {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 600;
          color: var(--skz-color-text-main);
          white-space: nowrap;
        }

        .divider {
          width: 1px;
          height: 24px;
          background-color: var(--skz-border-color-main);
          flex-shrink: 0;
        }

        .actions {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .cancel-btn {
          font-size: 13px;
          font-weight: 500;
          color: var(--skz-color-text-secondary, #6b7280);
          padding: 0 10px;
          height: 32px;
          line-height: 32px;
          border-radius: 8px;

          &:hover {
            background-color: rgba(0, 0, 0, 0.04);
            color: var(--skz-color-text-main);
          }
        }
      }
    `,
  ],
  animations: [
    trigger('slideUp', [
      transition(':enter', [
        style({ transform: 'translate(-50%, 100%)', opacity: 0 }),
        animate('200ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ transform: 'translate(-50%, 0)', opacity: 1 })),
      ]),
      transition(':leave', [animate('200ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ transform: 'translate(-50%, 100%)', opacity: 0 }))]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkActionBarComponent implements OnDestroy {
  contentState = inject(ContentStateService);
  private chunkService = inject(ChunkService);
  private confirmService = inject(AppConfirmService);
  private toastService = inject(ToastService);
  private translateService = inject(TranslateService);

  isAllSelected = computed(() => {
    const chunks = this.contentState.content().filter((c) => !c.isPlaceHolder);
    const selected = this.contentState.selectedChunks();
    return chunks.length > 0 && chunks.every((c) => selected.includes(c._id));
  });

  isIndeterminate = computed(() => {
    const chunks = this.contentState.content().filter((c) => !c.isPlaceHolder);
    const selected = this.contentState.selectedChunks();
    return selected.length > 0 && selected.length < chunks.length;
  });

  toggleSelectAll(checked: boolean): void {
    if (checked) {
      const allIds = this.contentState
        .content()
        .filter((c) => !c.isPlaceHolder)
        .map((c) => c._id);
      this.contentState.selectAllChunks(allIds);
    } else {
      this.contentState.clearChunkSelection();
    }
  }

  private translationSubscription: Subscription | null = null;

  ngOnDestroy(): void {
    if (this.translationSubscription) {
      this.translationSubscription.unsubscribe();
      this.translationSubscription = null;
    }
  }

  deleteSelected(): void {
    const selectedChunkIds = this.contentState.selectedChunks();
    const contentId = this.contentState.contentId;

    if (!selectedChunkIds.length || !contentId) {
      return;
    }

    this.confirmService.confirm({ message: this.translateService.instant('Confirm.DeleteSelectedChunks') }).subscribe((res) => {
      if (res) {
        // Retrieve the type for each chunk from the state to provide to deleteChunk
        const chunksToDelete = selectedChunkIds.map((id) => {
          const chunk = this.contentState.content().find((c) => c._id === id);
          return { chunkID: id, contentID: contentId, type: chunk?.type || 'text' };
        });

        // Use forkJoin to delete concurrently
        const deleteRequests = chunksToDelete.map((params) => this.chunkService.deleteChunk(params));

        forkJoin(deleteRequests).subscribe(() => {
          this.toastService.snackbar.open(this.translateService.instant('Snackbar.DeleteSelectedChunksSuccess'), 'Ok', { duration: 2000 });
          this.contentState.exitMultiSelectMode();
          this.contentState.getPieces(true, true);
        });
      }
    });
  }

  translateSelected(): void {
    const selectedChunkIds = this.contentState.selectedChunks();
    const contentId = this.contentState.contentId;

    const chunksToTranslate = selectedChunkIds
      .map((id) => this.contentState.content().find((c) => c._id === id))
      .filter((chunk): chunk is any => !!(chunk && chunk.editions && chunk.editions.length > 0 && (chunk.type === 'text' || chunk.type === 'html')));

    if (!chunksToTranslate.length) {
      this.toastService.snackbar.open(this.translateService.instant('Snackbar.NoTranslatableElementsSelected'), 'Ok', { duration: 2000 });
      return;
    }

    this.confirmService.confirm({ message: this.translateService.instant('Confirm.TranslateSelectedChunks') }).subscribe((res) => {
      if (res) {
        this.contentState.setLoading({ status: 'loading', message: this.translateService.instant('Loader.TranslationInProgress'), reason: 'generate-translation' });

        const sourceLanguage = this.translateService.currentLang;
        const targetLanguage = sourceLanguage === 'fr' ? 'en' : 'fr';

        const translationRequests = chunksToTranslate.map((chunk) => {
          const sourceEdition = chunk.editions.find((ed: any) => ed.language === sourceLanguage);
          const sourceVersion = sourceEdition?.__v || 1;

          return this.chunkService.translateChunk({
            chunkId: chunk._id,
            version: sourceVersion,
            editions: chunk.editions,
            type: chunk.type,
            targetLanguage: targetLanguage,
            contentId: contentId,
          });
        });

        if (this.translationSubscription) {
          this.translationSubscription.unsubscribe();
        }

        this.translationSubscription = forkJoin(translationRequests).subscribe({
          next: () => {
            this.toastService.snackbar.open(this.translateService.instant('Snackbar.BulkTranslateStarted'), 'Ok', { duration: 3000 });
            this.contentState.exitMultiSelectMode();
            this.contentState.getPieces(true, true);
          },
          error: (err) => {
            console.error('Bulk translation failed', err);
            this.contentState.setLoading({ status: 'idle' });
            this.toastService.snackbar.open(this.translateService.instant('Snackbar.TranslationFail'), 'Ok', { duration: 2000 });
          },
        });
      }
    });
  }

  generateAudioForSelected(): void {
    const selectedChunkIds = this.contentState.selectedChunks();
    const contentId = this.contentState.contentId;

    if (!selectedChunkIds.length || !contentId) {
      return;
    }

    this.confirmService.confirm({ message: this.translateService.instant('Confirm.GenerateAudioSelectedChunks') }).subscribe((res) => {
      if (res) {
        this.contentState.setLoading({ status: 'loading', message: this.translateService.instant('Loader.Saving'), reason: 'generate-audio' });

        const targetLang = this.translateService.currentLang;

        this.chunkService.generateAllAudios(contentId, targetLang, selectedChunkIds).subscribe({
          next: (res: any) => {
            if (res && res.count > 0) {
              this.toastService.snackbar.open(this.translateService.instant('ChunkController.Options.GenerateAllAudioStarted'), 'Ok', { duration: 3000 });
            } else {
              this.toastService.snackbar.open(this.translateService.instant('Snackbar.AllSelectedAudiosUpToDate'), 'Ok', { duration: 3000 });
            }
            this.contentState.exitMultiSelectMode();
            this.contentState.setLoading({ status: 'idle' });
          },
          error: (err) => {
            console.error('Bulk TTS generation failed', err);
            this.contentState.setLoading({ status: 'idle' });
            this.toastService.snackbar.open(this.translateService.instant('Snackbar.VoiceGenerationFail'), 'Ok', { duration: 2000 });
          },
        });
      }
    });
  }
}
